import { type ReactElement, createElement, type FC, Fragment } from "react";
import {
	type ISbRichtext,
	type ISbStoryData,
	type StoryblokRichTextNode,
	type StoryblokRichTextOptions,
	convertAttributesInElement,
	richTextResolver,
	MarkTypes,
	BlockTypes,
} from "@storyblok/react";
import Link from "../Link";
import {
	cn,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from "@babybjorn/ui";
import * as s from "./RichText.module.scss";

type RichTextProps = {
	content?: ISbRichtext | string | null;
	className?: string;
	centeredText?: boolean | null;
};

type BasicLink = {
	linktype?: string | undefined;
	href?: string | undefined;
	target?: string | undefined;
	anchor?: string | undefined;
	uuid?: string | undefined;
};

type StoryLink = BasicLink & {
	story: ISbStoryData;
};

const isStoryLink = (link: BasicLink): link is StoryLink =>
	link.linktype === "story" && "story" in link;

const addHttpsToUrl = (url: string) =>
	/^https?:\/\//i.test(url) ? url : `https://${url}`;

const RichText: FC<RichTextProps> = ({ content, className, centeredText }) => {
	if (!content) return null;
	if (typeof content === "string") {
		return (
			<p
				className={cn(s.richtext, className, {
					[s.centeredText]: centeredText,
				})}
			>
				{content}
			</p>
		);
	}

	const options: StoryblokRichTextOptions<ReactElement> = {
		renderFn: createElement,
		textFn: (text: string, attrs?: Record<string, ISbRichtext>) =>
			createElement(Fragment, attrs, text),
		keyedResolvers: true,
		resolvers: {
			[MarkTypes.LINK]: (node: StoryblokRichTextNode<ReactElement>) => {
				const link = node.attrs as BasicLink;
				const { href, target, anchor, linktype } = link;

				const translatedSlug = isStoryLink(link)
					? link.story.full_slug
					: undefined;

				const fixedHref =
					linktype === "url" && target === "_blank"
						? addHttpsToUrl(translatedSlug ?? href ?? "")
						: (translatedSlug ?? href ?? "");

				return (
					<Link
						anchor={anchor}
						href={fixedHref}
						linktype={linktype}
						target={target}
					>
						{node.text}
					</Link>
				);
			},
			[BlockTypes.TABLE]: (node: StoryblokRichTextNode<ReactElement>) => {
				const body = <TableBody>{node.children}</TableBody>;

				return <Table>{body}</Table>;
			},
			[BlockTypes.TABLE_ROW]: (node: StoryblokRichTextNode<ReactElement>) => {
				const { ...rest } = node.attrs || {};

				return <TableRow {...rest}>{node.children}</TableRow>;
			},
			[BlockTypes.TABLE_CELL]: (node: StoryblokRichTextNode<ReactElement>) => {
				const { className, ...rest } = node.attrs || {};

				return (
					<TableCell
						className={cn(
							"min-w-30",
							"[&>*]:flex [&>*]:flex-col [&>*]:justify-center",
							"[&_img]:w-40 md:[&_img]:w-60 [&>*]:p-2",
							className,
						)}
						{...rest}
					>
						{node.children}
					</TableCell>
				);
			},
			[BlockTypes.TABLE_HEADER]: (
				node: StoryblokRichTextNode<ReactElement>,
			) => {
				const { ...rest } = node.attrs || {};

				return (
					<TableHead
						className={cn({ "text-center": node.attrs?.colspan > 1 })}
						{...rest}
					>
						{node.children}
					</TableHead>
				);
			},
		},
	};

	const html = richTextResolver<ReactElement>(options).render(
		content as StoryblokRichTextNode<ReactElement>,
	);

	const formattedHtml = convertAttributesInElement(html);

	return (
		<div
			className={cn(s.richtext, className, {
				[s.centeredText]: centeredText,
			})}
		>
			{formattedHtml}
		</div>
	);
};

export default RichText;
